// frontend/src/operator/OperatorPasswordChange.js
// 現在ログイン中の運営者アカウントのパスワードを変更する

import { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

const OPERATOR_PATH = process.env.REACT_APP_OPERATOR_PATH || 'ops-7k3m9x2p4r8q';

export default function OperatorPasswordChange() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checking, setChecking] = useState(true);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        setEmail(data.session.user.email || '');
        setSignedIn(true);
      }
      setChecking(false);
    })();
  }, []);

  if (checking) return <div style={styles.center}>確認中...</div>;
  if (!signedIn) return <Navigate to={`/${OPERATOR_PATH}/login`} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!current || !next || !confirm) return setError('全項目を入力してください');
    if (next.length < 8) return setError('新パスワードは 8 文字以上必要です');
    if (next !== confirm) return setError('新パスワードと確認が一致しません');
    if (current === next) return setError('現在と同じパスワードは設定できません');

    setSubmitting(true);
    try {
      // 1. 現在のパスワードで再認証
      const { error: authErr } = await supabase.auth.signInWithPassword({
        email, password: current,
      });
      if (authErr) throw new Error('現在のパスワードが正しくありません');

      // 2. 新パスワードに更新
      const { error: updErr } = await supabase.auth.updateUser({ password: next });
      if (updErr) throw new Error(`更新失敗: ${updErr.message}`);

      setSuccess('✅ パスワードを変更しました。 次回ログインから新パスワードを使ってください。');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => navigate(`/${OPERATOR_PATH}`)} style={styles.backBtn}>← ダッシュボード</button>
          <h1 style={styles.title}>🔑 パスワード変更</h1>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.card}>
          <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
            現在ログイン中: <strong style={{ color: '#e2e8f0' }}>{email}</strong>
          </div>

          <form onSubmit={handleSubmit}>
            <label style={styles.field}>
              <div style={styles.lbl}>現在のパスワード</div>
              <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                required autoComplete="current-password" style={styles.input} />
            </label>
            <label style={styles.field}>
              <div style={styles.lbl}>新しいパスワード (8 文字以上)</div>
              <input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                required minLength={8} autoComplete="new-password" style={styles.input} />
            </label>
            <label style={styles.field}>
              <div style={styles.lbl}>新しいパスワード (確認)</div>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                required minLength={8} autoComplete="new-password" style={styles.input} />
            </label>

            {error && <div style={styles.errorBox}>{error}</div>}
            {success && <div style={styles.successBox}>{success}</div>}

            <button type="submit" disabled={submitting} style={{
              ...styles.submitBtn,
              background: submitting ? '#475569' : styles.submitBtn.background,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}>
              {submitting ? '変更中...' : 'パスワードを変更'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#0f172a', color: '#e2e8f0' },
  center: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#fff' },
  header: { background: '#1e293b', padding: '16px 24px', borderBottom: '1px solid #334155' },
  title: { margin: 0, fontSize: 20, fontWeight: 700 },
  backBtn: { padding: '6px 14px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  main: { maxWidth: 480, margin: '40px auto', padding: '0 20px' },
  card: { background: '#1e293b', borderRadius: 12, padding: 28, border: '1px solid #334155' },
  field: { display: 'block', marginBottom: 16 },
  lbl: { fontSize: 12, color: '#94a3b8', marginBottom: 6, fontWeight: 600 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #475569', background: '#0f172a', color: '#e2e8f0', fontSize: 14, boxSizing: 'border-box' },
  errorBox: { background: 'rgba(239,68,68,0.15)', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: 12, marginBottom: 12 },
  successBox: { background: 'rgba(34,197,94,0.15)', color: '#86efac', padding: '10px 14px', borderRadius: 8, fontSize: 12, marginBottom: 12 },
  submitBtn: { width: '100%', padding: '11px', background: 'linear-gradient(135deg, #1d4ed8, #1e40af)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14 },
};
